// Formatting + chart-transform helpers shared by the AI CIO block renderer.
// The orchestrator's numeric conventions (fractions for percent, etc.) are
// documented in the iq-ai-cio FRONTEND_INTEGRATION guide.

import type { TFunction } from 'i18next';

/** Chart palette using the app's --chart-* tokens (matches shared/charts). */
export const CHART_PALETTE = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function colorAt(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length] ?? 'hsl(var(--chart-1))';
}

function isNum(v: unknown): v is number {
  return typeof v === 'number' && !Number.isNaN(v);
}

/** Coerce an unknown value to a display string without "[object Object]". */
export function safeString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/**
 * Returns `url` only if it uses a safe scheme — http/https, or a relative path
 * that inherits the page origin. Returns null for everything else (javascript:,
 * data:, vbscript:, blob:, mailto:, …). Guards against rendering an
 * orchestrator-controlled URL straight into an `<a href>`, which a `javascript:`
 * or `data:text/html` value would turn into a click-to-XSS sink.
 */
export function safeHref(url: unknown): string | null {
  if (typeof url !== 'string' || !url.trim()) return null;
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    const { protocol } = new URL(url, base);
    return protocol === 'http:' || protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * When a FILE block's signed link (`payload.expires_at`) stops working, in epoch
 * ms, or null when the block carries no readable time. The compute layer stamps
 * `%Y-%m-%dT%H:%M:%SZ`; a time with no zone designator is read as UTC, not as the
 * viewer's local time, which would move the expiry by their offset.
 */
export function linkExpiryMs(expiresAt: unknown): number | null {
  if (typeof expiresAt !== 'string' || !expiresAt.trim()) return null;
  const iso = expiresAt.trim();
  const ms = Date.parse(/(?:z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** Metric value formatter — `%` unit means the value is a 0–1 fraction. */
export function formatMetricValue(
  value: unknown,
  unit: string | null | undefined,
  t: TFunction<'ai-cio'>,
): string {
  if (value === null || value === undefined || value === '') return t('block.notAvailable', 'N/A');
  if (isNum(value)) {
    if (unit === '%') return `${(value * 100).toFixed(2)}%`;
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return safeString(value);
}

/** Table cell formatter, keyed by the column `type`. */
export function formatCell(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'percent':
      return isNum(value) ? `${(value * 100).toFixed(2)}%` : safeString(value);
    case 'currency':
      return isNum(value)
        ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : safeString(value);
    case 'number':
      return isNum(value) ? value.toLocaleString() : safeString(value);
    case 'date':
      try {
        return new Intl.DateTimeFormat().format(new Date(safeString(value)));
      } catch {
        return safeString(value);
      }
    default:
      return safeString(value);
  }
}

/** Sign-based color class for percent/delta cells. Empty string = neutral. */
export function signClass(value: unknown): string {
  const n = typeof value === 'number' ? value : parseFloat(safeString(value));
  if (Number.isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-success' : 'text-destructive';
}

// ── Chart spec transform (vega-lite-ish → recharts-friendly) ────────────────

type Row = Record<string, unknown>;

interface VegaSpec {
  mark?: string | { type?: string };
  encoding?: {
    x?: { field?: string };
    y?: { field?: string };
  };
  data?: { values?: Row[] };
  layer?: VegaSpec[];
}

const MARK_RANK: Record<string, number> = { line: 0, area: 1, bar: 2, arc: 3 };

function markName(mark: VegaSpec['mark']): string {
  return typeof mark === 'string' ? mark : (mark?.type ?? '');
}

/** Flattens a (possibly layered) vega spec to a single primary layer. */
function extractPrimaryLayer(spec?: VegaSpec): {
  data: Row[];
  mark: string;
  encoding: VegaSpec['encoding'];
} {
  if (!spec) return { data: [], mark: '', encoding: undefined };
  if (Array.isArray(spec.layer)) {
    const withData = spec.layer.filter((l) => (l.data?.values?.length ?? 0) > 0);
    const primary = withData.sort(
      (a, b) => (MARK_RANK[markName(a.mark)] ?? 4) - (MARK_RANK[markName(b.mark)] ?? 4),
    )[0];
    return {
      data: primary?.data?.values ?? [],
      mark: markName(primary?.mark),
      encoding: primary?.encoding,
    };
  }
  return { data: spec.data?.values ?? [], mark: markName(spec.mark), encoding: spec.encoding };
}

export type ChartKind = 'line' | 'bar' | 'area' | 'pie' | 'donut';

export interface NormalizedChart {
  kind: ChartKind;
  data: Row[];
  xKey: string;
  series: { key: string; label: string; color?: string }[];
  /** Pie/donut slices. */
  pie: { name: string; value: number; amount?: number; color?: string }[];
  /** y values look like 0–1 fractions → render as %. */
  asPercent: boolean;
  unit?: string;
}

function inferKindFromMark(mark: string): ChartKind | undefined {
  if (mark === 'arc') return 'donut';
  if (mark === 'line') return 'line';
  if (mark === 'bar') return 'bar';
  if (mark === 'area') return 'area';
  return undefined;
}

function toPieItems(data: Row[]): NormalizedChart['pie'] {
  return data.map((d) => ({
    name: safeString(d.name ?? d.label ?? d.category),
    value: Number(d.value ?? d.percent ?? 0),
    amount: typeof d.amount === 'number' ? d.amount : undefined,
    color: typeof d.color === 'string' ? d.color : undefined,
  }));
}

/**
 * Normalizes a chart block's payload+metadata into a recharts-ready shape,
 * accepting both the vega `{spec_type, spec}` form and the flat
 * `{data, series, x_key, items}` form.
 */
export function normalizeChart(
  payload: Record<string, unknown>,
  metadata: Record<string, unknown>,
): NormalizedChart {
  const spec = payload.spec as VegaSpec | undefined;
  const primary = extractPrimaryLayer(spec);
  const metaKind = typeof metadata.chart_type === 'string' ? metadata.chart_type : undefined;
  const kind: ChartKind =
    (metaKind === 'pie' || metaKind === 'donut' ? metaKind : (metaKind as ChartKind | undefined)) ??
    inferKindFromMark(primary.mark) ??
    'bar';

  const data = (payload.data as Row[] | undefined) ?? primary.data;

  if (kind === 'pie' || kind === 'donut') {
    const pie = (payload.items as NormalizedChart['pie'] | undefined) ?? toPieItems(data);
    return { kind, data, xKey: 'name', series: [], pie, asPercent: false };
  }

  // XY axes
  const firstRow = data[0] ?? {};
  const keys = Object.keys(firstRow);
  const xKey =
    primary.encoding?.x?.field ??
    (typeof payload.x_key === 'string' ? payload.x_key : undefined) ??
    keys.find((k) => typeof firstRow[k] === 'string') ??
    keys[0] ??
    'x';
  const yKeyGuess =
    primary.encoding?.y?.field ??
    keys.find((k) => typeof firstRow[k] === 'number') ??
    keys[1] ??
    'y';

  const series = (payload.series as NormalizedChart['series'] | undefined) ?? [
    {
      key: yKeyGuess,
      label: typeof metadata.y_label === 'string' ? metadata.y_label : yKeyGuess,
    },
  ];

  // Fraction detection: all y values |v| < 1.5 → percent.
  const asPercent = series.every((s) =>
    data.every((d) => {
      const v = d[s.key];
      return typeof v === 'number' && Math.abs(v) < 1.5;
    }),
  );
  const unit =
    (typeof metadata.unit === 'string' ? metadata.unit : undefined) ??
    (asPercent ? '%' : undefined);

  return { kind, data, xKey, series, pie: [], asPercent, unit };
}
